import React from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  Divider,
  Container,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { AppColors } from '../theme/colors';

const CommercialLawPage: React.FC = () => {
  const navigate = useNavigate();

  const infoData = [
    ['販売業者', 'レフリア株式会社（Lefria Inc.）'],
    ['代表責任者', '代表取締役 坂本明仁'],
    ['所在地', '〒121-0816 東京都足立区竹の塚三丁目２０番９号２Ｆ'],
    ['電話番号', '03-5856-7380（平日10:00〜17:00）'],
    ['メールアドレス', 'info@lefria.com'],
    ['サイトURL', 'https://lefria.com/'],
    // 以前の文言:
    // ['商品販売価格', '月額¥12,000（税抜）〜\n※詳細なプランは料金表をご確認ください。'],
    ['商品販売価格', '月額¥12,000（税抜）〜'],
    ['商品代金以外の料金', '当サイトのページの閲覧、サービスのご利用に必要となるインターネット接続料金、通信料金は、お客様のご負担となります。'],
    ['支払方法', 'クレジットカード決済、銀行振込'],
    ['支払時期', 'お申し込み当月は無料期間とし、翌月1日に初回決済を行います。\n以降、毎月１日に自動決済いたします。（無料期間は申込日の月末まで）'],
    ['商品の引渡時期', 'ご注文後、即座にサービスをご利用いただけます。'],
    ['ソフトウェアの動作環境', 'OS: Windows 10/11, macOS 12以降, iOS 13以降, Android 8.0以降\nブラウザ: Google Chrome, Safari, Microsoft Edge, Firefox（各最新版）\nその他: セキュリティソフト等の設定により正常に動作しない場合があります。'],
  ];

  const buildInfoTable = () => {
    return infoData.map(([label, value], index) => (
      <Box key={index} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'flex-start' },
            borderBottom: '1px solid #E5E7EB',
            pb: 2,
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: '200px' },
              minWidth: { xs: 'auto', sm: '200px' },
              mb: { xs: 1, sm: 0 },
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: AppColors.textPrimary,
                lineHeight: 1.5,
              }}
            >
              {label}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: 14,
                color: AppColors.textPrimary,
                lineHeight: 1.6,
                whiteSpace: 'pre-line',
              }}
            >
              {value}
            </Typography>
          </Box>
        </Box>
      </Box>
    ));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface }}>
      {/* ヘッダー */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: AppColors.primary,
          boxShadow: 'none',
          zIndex: 1000,
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: 'white',
            }}
          >
            特定商取引法に基づく表記
          </Typography>
        </Toolbar>
      </AppBar>

      {/* メインコンテンツ */}
      <Box sx={{ pt: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            {/* タイトル */}
            <Box sx={{ py: 2 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: 24,
                  fontWeight: 'bold',
                  color: AppColors.textPrimary,
                  mb: 2,
                }}
              >
                特定商取引法に基づく表記
              </Typography>
              <Divider sx={{ height: 1, bgcolor: '#E5E7EB' }} />
            </Box>

            {/* 情報テーブル */}
            <Card
              sx={{
                mt: 2,
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {buildInfoTable()}
              </CardContent>
            </Card>

            {/* 注意事項 */}
            <Box
              sx={{
                mt: 2,
                p: 2.5,
                bgcolor: AppColors.primaryLighter,
                borderRadius: 2,
                border: `1px solid ${AppColors.primary}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 'bold',
                  color: AppColors.textPrimary,
                  mb: 1.5,
                }}
              >
                注意事項
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: '#374151',
                  lineHeight: 1.6,
                }}
              >
                • 本サービスは法人向けのコミュニケーションプラットフォームです<br />
                • 個人情報の取り扱いについては、プライバシーポリシーをご確認ください<br />
                • サービス利用に関するお問い合わせは、上記メールアドレスまでご連絡ください
              </Typography>
            </Box>
            {/* 会社登録画面への導線。以前は「プラン選択はこちら」としていましたが、
                文言のみ「会社登録はこちら」に変更し、旧ラベルはコメントとして保持しています。 */}
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Button
                startIcon={<ArticleOutlinedIcon sx={{ color: AppColors.primary }} />}
                onClick={() => navigate('/company-register')}
                sx={{
                  color: AppColors.primary,
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: 'none',
                  textDecoration: 'underline',
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                {/* 以前の文言: プラン選択はこちら */}
                会社登録はこちら
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export { CommercialLawPage };
export default CommercialLawPage;
